<script setup lang="ts">
import type { Elemental } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import IconBox from '~~/src/components/DrawerSidebarRight/components/shared/IconBox.vue'
import ElemetalIconPicker from '~~/src/components/Elemental/ElemetalIconPicker/index.vue'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
const props = defineProps<{
  list: Elemental | List
  folderLevel: number
  columns: string[]
  elementalLabel: string
  isMain?: boolean
}>()

const { state } = useElementalListsStore()
const elementalTypes = useElementalTypes()
const columns = computed(() => {
  return (props.columns && props.columns.length > 0) ? props.columns : []
})

const elementalType = computed(() => {
  const type = elementalTypes.value.find(t => t.id === props.list.contentTypeId)

  if (props.list.isList && props.list.contentTypeId === ElementalTypeEnum.List)
    return 'Combined List'

  if (props.list.isList)
    return `${type?.name} list`

  return elementalTypes.value.find(t => t.id === props.list.elementalTypeId)?.name
})

const elementalColor = computed(() => {
  if (props.list.isList)
    return ElementalColors?.[props.list.contentTypeId as ElementalTypeEnum]

  return props.list.elementalTypeId !== undefined ? ElementalColors?.[props.list.elementalTypeId as ElementalTypeEnum] : null
})
</script>

<template>
  <div class="row-elemental">
    <div v-if="!isMain" class="drag-indicator">
      <div i-material-symbols:drag-indicator class="w-5 h-5" />
    </div>
    <div
      v-if="columns.includes('title')"
      class="elemental-cell title flex items-center gap-2 text-sm"
      :class="{
        'pl-4': folderLevel === 2,
        'pl-8': folderLevel === 3,
      }"
    >
      <slot name="list-icon">
        <IconBox class="h-7 w-7 flex-shrink-0 !p-0">
          <ElementalElemetalFolderIconPicker
            v-if="list.isList"
            :type="list.contentTypeId ?? 0"
          />
          <ElemetalIconPicker
            v-else
            :type="list.elementalTypeId ?? 0"
          />
        </IconBox>
      </slot>
      <div class="overflow-hidden pr-7">
        <UtilsHighLightText :text="list.title" :search="state.query" />
      </div>
    </div>

    <!-- author -->
    <div v-if="columns.includes('user') && list.user" class="elemental-cell user flex items-center gap-2 hide-column">
      <UserChip :user="list.user" variant="text" />
    </div>

    <!-- type -->
    <div v-if="columns.includes('elementalTypeId')" class="elemental-cell elementalTypeId flex items-center gap-2 hide-column">
      <IconBox
        :style="{
          backgroundColor: elementalColor?.[1] || '#ccc',
          color: elementalColor?.[0] || '#000',
          padding: '0',
        }"
      >
        <ElementalElemetalFolderIconPicker
          v-if="list.isList"
          :type="list.contentTypeId ?? 0"
        />
        <ElemetalIconPicker
          v-else
          :type="list.elementalTypeId ?? 0"
          w-7
          h-7
        />
      </IconBox>
      <span class="capitalize">
        {{ elementalType }}
      </span>
    </div>

    <!-- createdAt -->
    <div v-if="columns.includes('createdAt')" class="elemental-cell createdAt flex items-center gap-2 hide-column">
      {{ list.createdAt ? formatDateToShortUS(new Date(list.createdAt)) : '' }}
    </div>

    <!-- updatedAt -->
    <div v-if="columns.includes('updatedAt')" class="elemental-cell updatedAt flex items-center gap-2 hide-column">
      {{ list.updatedAt ? formatDateToShortUS(new Date(list.updatedAt)) : '' }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
